import React, { useEffect, useMemo, useRef } from 'react';

import { format, isSameDay, subDays } from 'date-fns';
import Linkify from 'linkify-react';
import { twMerge } from 'tailwind-merge';

import {
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  ClockIcon,
  FilmIcon,
  GlobeAltIcon,
  MapPinIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import ParticipantItem from '../search/ParticipantItem';
import Button from '../shared/Button';
import { IconButton } from '../shared/IconButton';
import { CalendarCategory, CalendarEvent, ProjectMember, ProjectRole, SceneCard } from './Calendar';
import { formatDates, convertUserTimeToEventTimezone } from './eventUtils';

interface EventDetailsItemProps {
  className?: string;
  children: React.ReactNode;
}

interface ParticipantListItemProps {
  email: string;
  projectMembersByEmail: Record<string, ProjectMember>;
}

interface EventDetailsProps {
  selectedEvent: CalendarEvent;
  projectMembersByEmail: Record<string, ProjectMember>;
  calendarCategoriesById: {
    [key: number]: CalendarCategory;
  };
  setMode: (mode: string) => void;
  setIsEventInfoOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isProjectAdmin: boolean;
  sceneCard?: SceneCard;
  handleSceneCardRedirect: (id: string) => void;
  projectRole: ProjectRole;
}

function formatTimezoneForDisplay(timezone: string): string {
  if (!timezone || timezone.trim() === '') return '';
  
  const parts = timezone.split('/');
  if (parts.length > 1) {
    return parts[parts.length - 1].replace(/_/g, ' ');
  }
  return timezone;
}

function getTimezoneAbbr(timezone: string): string {
  if (!timezone || timezone.trim() === '') return '';

  const commonAbbrs: Record<string, string> = {
    'America/New_York': 'EST',
    'America/Chicago': 'CST',
    'America/Denver': 'MST',
    'America/Los_Angeles': 'PST',
    'America/Phoenix': 'MST',
    'America/Anchorage': 'AKST',
    'America/Honolulu': 'HST',
    'Pacific/Honolulu': 'HST',
    'Europe/London': 'GMT',
    'Europe/Paris': 'CET',
    'Europe/Berlin': 'CET',
    'Europe/Rome': 'CET',
    'Europe/Moscow': 'MSK',
    'Asia/Dubai': 'GST',
    'Asia/Tokyo': 'JST',
    'Asia/Shanghai': 'CST',
    'Asia/Kolkata': 'IST',
    'Asia/Hong_Kong': 'HKT',
    'Asia/Singapore': 'SGT',
    'Australia/Sydney': 'AEST',
    'Australia/Melbourne': 'AEST',
    'Pacific/Auckland': 'NZST',
    'UTC': 'UTC',
  };

  if (commonAbbrs[timezone]) {
    return commonAbbrs[timezone];
  }

  const parts = timezone.split('/');
  if (parts.length > 1) {
    const lastPart = parts[parts.length - 1];
    return lastPart.substring(0, 3).toUpperCase();
  }

  return timezone.substring(0, 3).toUpperCase();
}

function EventDetailsItem({ className, children }: EventDetailsItemProps) {
  return (
    <div
      className={twMerge(
        'flex items-center gap-2 text-sm leading-5 font-medium text-gray-700',
        className
      )}
    >
      {children}
    </div>
  );
}

function ParticipantListItem({ projectMembersByEmail, email }: ParticipantListItemProps) {
  const projectMember = projectMembersByEmail[email];
  if (!projectMember) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-block h-8 w-8 overflow-hidden rounded-full bg-gray-100">
          <svg className="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        </span>
        <div className="text-sm leading-5 font-medium text-gray-700">{email}</div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <ParticipantItem
        imageUrl={projectMember.imageUrl}
        name={projectMember.name}
        email={projectMember.email}
        role={projectMember.role}
      />
    </div>
  );
}

export default function EventDetails({
  selectedEvent,
  projectMembersByEmail,
  calendarCategoriesById,
  setMode,
  setIsEventInfoOpen,
  isProjectAdmin,
  sceneCard,
  handleSceneCardRedirect,
  projectRole,
}: EventDetailsProps) {
  const sceneCardIdRef = useRef('');
  if (!selectedEvent) return null;
  
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { allDay, title, calendarCategoryId, start, end, location, description, participants, timezone, originalTimezone } =
    selectedEvent;

  const { parsedStart, parsedEnd } = formatDates({ start, end, allDay });
  console.log('=== EventDetails DEBUG ===');
  console.log('Event data:', {
    id: selectedEvent?.id,
    allDay: allDay,
    originalStart: start,
    originalEnd: end,
    parsedStart: parsedStart?.toString(),
    parsedEnd: parsedEnd?.toString(),
    sameDay: isSameDay(parsedStart, parsedEnd) // You might need to calculate this
  });
  let displayEnd = parsedEnd;
  let sameDay = false;

  if (allDay && parsedStart && parsedEnd) {
    const startDateOnly = new Date(
      parsedStart.getFullYear(),
      parsedStart.getMonth(),
      parsedStart.getDate()
    );
    const endDateOnly = new Date(
      parsedEnd.getFullYear(),
      parsedEnd.getMonth(),
      parsedEnd.getDate()
    );
    sameDay = startDateOnly.getTime() === endDateOnly.getTime();
  } else if (parsedStart && parsedEnd) {
    sameDay = isSameDay(parsedStart, parsedEnd);
  }

  const canUpdateEvent = useMemo(() => {
    const allowedRoles: ProjectRole[] = [
      'production_manager',
      'coordinator',
      'stunt_office_coordinator',
      'assistant_director',
    ];
    if (isProjectAdmin || allowedRoles.includes(projectRole)) return true;
    if (projectRole === 'hair_and_makeup' || projectRole === 'wardrobe') {
      const id = selectedEvent.calendarCategoryId;
      const category = calendarCategoriesById[id];
      return category.name === 'Hair & Makeup Fittings' || category.name === 'Costume Fittings';
    }
    return false;
  }, [isProjectAdmin, projectRole, selectedEvent, calendarCategoriesById]);

  useEffect(() => {
    sceneCardIdRef.current = sceneCard?.uuid;
  }, [sceneCard]);

  const showOriginalTime = originalTimezone && 
                         originalTimezone.trim() !== '' &&
                         originalTimezone !== userTimezone && 
                         !allDay &&
                         parsedStart && !isNaN(parsedStart.getTime()) &&
                         parsedEnd && !isNaN(parsedEnd.getTime());
  let originalStartTime = '';
  let originalEndTime = '';

  if (showOriginalTime) {
    try {
      const originalStart = convertUserTimeToEventTimezone(parsedStart, userTimezone, originalTimezone);
      const originalEnd = convertUserTimeToEventTimezone(parsedEnd, userTimezone, originalTimezone);
      
      if (originalStart && !isNaN(originalStart.getTime())) {
        originalStartTime = format(originalStart, 'h:mm a');
      }
      if (originalEnd && !isNaN(originalEnd.getTime())) {
        originalEndTime = format(originalEnd, 'h:mm a');
      }
    } catch (error) {
      console.error('Error calculating original time:', error);
      originalStartTime = '';
      originalEndTime = '';
    }
  }

  const handleProfileNavigation = (uuid: string) => {
    if (uuid) {
      window.open(`/users/${uuid}?from=directory`, '_blank');
    }
  };

  return (
    <div className="flex flex-col gap-2 relative h-full w-full lg:w-72">
      <div className="border-b p-4 flex items-center justify-between fixed top-18 w-full lg:static lg:w-72 bg-white shadow-sm">
        <div className="text-lg leading-6 font-medium text-gray-700">{title}</div>
        <button onClick={() => setIsEventInfoOpen(false)}>
          <XMarkIcon className="w-6 h-6 text-gray-500" />
        </button>
      </div>
      <div className="overflow-y-auto h-full pb-24">
        <div className="flex flex-col gap-4 p-4 mt-20 lg:mt-0">
          <div className="flex items-center gap-2">
            <div
              className="h-4 w-4 rounded-md"
              style={{ backgroundColor: calendarCategoriesById[calendarCategoryId].color }}
            />
            <EventDetailsItem>{calendarCategoriesById[calendarCategoryId].name}</EventDetailsItem>
          </div>
        </div>
        {sceneCard && (
          <div className="border-t py-4 px-2">
            <div className="border bg-gray-50 rounded flex items-center justify-between p-2">
              <div className="flex items-center gap-2">
                <FilmIcon className="text-gray-500 h-5 w-5" />
                <div className="flex items-center gap-1">
                  <span className="text-sm leading-5 font-medium text-gray-700">
                    {sceneCard.scene}
                  </span>
                  <span className="text-xs leading-4 font-medium text-gray-500">
                    {sceneCard.scene_name}
                  </span>
                </div>
              </div>
              <IconButton onClick={() => handleSceneCardRedirect(sceneCardIdRef.current)}>
                <ArrowTopRightOnSquareIcon className="w-5 h-5 text-blue-500" />
              </IconButton>
            </div>
          </div>
        )}
        <div className="border-t p-4 flex flex-col gap-2">
          <EventDetailsItem>
            <CalendarDaysIcon className="text-gray-500 h-5 w-5 flex-shrink-0" />
            <div className="flex items-center gap-2">
              {parsedStart && !isNaN(parsedStart.getTime()) ? (
                <>
                  <span>{format(parsedStart, 'PP')}</span>
                  {displayEnd && !isNaN(displayEnd.getTime()) && !sameDay && (
                    <>
                      <ArrowRightIcon className="w-4 h-4 text-gray-500" />
                      <span>
                         {format(displayEnd, 'PP')}
                      </span>
                    </>
                  )}
                </>
              ) : (
                <span>Invalid date</span>
              )}
            </div>
          </EventDetailsItem>
          {!selectedEvent.allDay && parsedStart && !isNaN(parsedStart.getTime()) && (
            <EventDetailsItem>
              <ClockIcon className="text-gray-500 h-4 w-4" />
              <span>{format(parsedStart, 'hh:mm aaa')}</span>
              {parsedEnd && !isNaN(parsedEnd.getTime()) && <ArrowRightIcon className="w-4 h-4 text-gray-500" />}
              {parsedEnd && !isNaN(parsedEnd.getTime()) && <span>{format(parsedEnd, 'hh:mm aaa')}</span>}
            </EventDetailsItem>
          )}

          {originalTimezone && originalTimezone.trim() !== '' && (
            <EventDetailsItem>
              <GlobeAltIcon className="text-gray-500 h-4 w-4" />
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  Timezone: {getTimezoneAbbr(originalTimezone)}
                </span>
                {originalTimezone !== userTimezone && (
                  <span className="text-xs text-gray-500">
                    Displayed in your timezone: {getTimezoneAbbr(userTimezone)}
                  </span>
                )}
              </div>
            </EventDetailsItem>
          )}
          {showOriginalTime && originalStartTime && originalEndTime && originalTimezone.trim() !== '' && (
            <EventDetailsItem className="text-sm text-gray-600 ml-6">
              <span>
                Original event time: {originalStartTime} - {originalEndTime} ({getTimezoneAbbr(originalTimezone)})
              </span>
            </EventDetailsItem>
          )}
        </div>
        
        {location && (
          <div className="border-t p-4">
            <EventDetailsItem>
              <MapPinIcon className="text-gray-500 h-5 w-5 flex-shrink-0" />
              {location}
            </EventDetailsItem>
          </div>
        )}
        {participants && participants.length > 0 && (
          <div className="border-t p-4 flex flex-col gap-2">
            <div className="text-sm leading-5 font-medium text-gray-900 mb-2">
              Participants ({participants.length})
            </div>
            {participants.map((participant) => {
              const member = projectMembersByEmail[participant];
              return (
                <div
                  key={participant}
                  onClick={(e) => {
                    e.preventDefault();
                    
                    const uuid = member?.uuid;

                    if (uuid) {
                      handleProfileNavigation(uuid);
                    } else {
                      console.error('No UUID found for member:', member);
                    }
                  }}
                  className={twMerge(
                    'cursor-pointer transition hover:bg-gray-50 rounded-md p-1',
                    !member && 'cursor-default hover:bg-transparent'
                  )}
                >
                  <ParticipantListItem
                    projectMembersByEmail={projectMembersByEmail}
                    email={participant}
                  />
                </div>
              );
            })}
          </div>
        )}
        {description && (
          <div className="border-t p-4">
            <div className="text-sm leading-5 font-medium">Notes</div>
             <div className="whitespace-pre-wrap break-words font-normal vinod flex items-center gap-2 text-sm leading-5 font-medium text-gray-700"
                dangerouslySetInnerHTML={{
                __html: description,
              }}
              >
              </div>
            {/* <EventDetailsItem className="whitespace-pre-wrap break-words font-normal">
              <div className="vinod1">
                <Linkify
                  options={{
                    className: 'text-blue-500 hover:text-blue-700',
                    target: '_blank',
                  }}
                >
                  {description}
                </Linkify>
              </div>
            </EventDetailsItem> */}
          </div>
        )}
      </div>
      {canUpdateEvent && (
        <div className="px-4 shadow-2xl border-t flex items-center bg-white h-20 w-full lg:w-72">
          <Button
            className="flex items-center gap-2 flex-grow-0"
            onClick={() => setMode('edit')}
            variant="secondary"
          >
            <PencilSquareIcon className="w-4 h-4 flex-shrink-0" />
            <span className="whitespace-nowrap">Edit event</span>
          </Button>
        </div>
      )}
    </div>
  );
}